'use client'

import { AnimatePresence, m, useReducedMotion } from 'motion/react'
import { useEffect, useState } from 'react'

import { Eyebrow } from '@/features/landing/shared/eyebrow'

/**
 * Editorial accordion. Hairline rules between items, serif questions,
 * a typographic "+ / —" marker on the right. No vermilion in chrome.
 * Entries open independently: a reader comparing two answers (Anki, then
 * WaniKani) keeps both, and a Path link never closes what was read. The
 * first three are open at rest (v1.2): the section opens as answers, not
 * as a list of questions to click through, and the three that answer
 * first are the ones a visitor weighs before scrolling on.
 */
const OPEN_AT_REST = [0, 1, 2]

export function FaqAccordion({
    items,
}: {
    items: ReadonlyArray<{ id: string; q: string; a: string }>
}) {
    const [open, setOpen] = useState<ReadonlySet<number>>(
        () => new Set(OPEN_AT_REST)
    )
    const reduce = useReducedMotion()
    const show = (i: number) => setOpen((prev) => new Set(prev).add(i))
    const toggle = (i: number) =>
        setOpen((prev) => {
            const next = new Set(prev)
            if (next.has(i)) next.delete(i)
            else next.add(i)
            return next
        })

    useEffect(() => {
        const openFromHash = () => {
            const hash = window.location.hash.slice(1)
            const i = items.findIndex((it) => it.id === hash)
            if (i >= 0) show(i)
        }
        // A second click on the same in-page link fires no hashchange (the
        // hash is unchanged), so the link click itself reopens the entry.
        const openFromClick = (e: MouseEvent) => {
            if (!(e.target instanceof Element)) return
            const a = e.target.closest('a[href^="#"]')
            if (!a) return
            const id = a.getAttribute('href')?.slice(1)
            const i = items.findIndex((it) => it.id === id)
            if (i >= 0) show(i)
        }
        openFromHash()
        window.addEventListener('hashchange', openFromHash)
        document.addEventListener('click', openFromClick)
        return () => {
            window.removeEventListener('hashchange', openFromHash)
            document.removeEventListener('click', openFromClick)
        }
    }, [items])

    return (
        <ul className="border-y border-ink/15">
            {items.map((item, i) => {
                const isOpen = open.has(i)
                const buttonId = `${item.id}-button`
                const panelId = `${item.id}-panel`
                const num = String(i + 1).padStart(2, '0')

                return (
                    <li
                        key={item.id}
                        id={item.id}
                        className={
                            i < items.length - 1 ? 'border-b border-ink/15' : ''
                        }
                    >
                        <h3 className="m-0">
                            <button
                                id={buttonId}
                                type="button"
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                onClick={() => toggle(i)}
                                className="grid w-full grid-cols-[auto_1fr_auto] items-baseline gap-5 px-4 py-6 text-left text-ink transition-colors hover:bg-paper-warm/40 focus-visible:ring-2 focus-visible:ring-vermilion focus-visible:ring-offset-2 focus-visible:outline-none sm:px-6"
                            >
                                <Eyebrow as="span" size="lg">
                                    {num}
                                </Eyebrow>
                                <span className="font-serif text-lp-question font-normal md:text-lp-question-md">
                                    {item.q}
                                </span>
                                <span
                                    aria-hidden
                                    className="relative inline-block font-serif text-lp-marker text-muted-fg tabular-nums"
                                >
                                    {/* invisible glyph sets the slot's intrinsic size + baseline */}
                                    <span className="invisible">+</span>
                                    <span
                                        className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ease-out motion-reduce:transition-none ${
                                            isOpen
                                                ? '-rotate-90 opacity-0'
                                                : 'rotate-0 opacity-100'
                                        }`}
                                    >
                                        +
                                    </span>
                                    <span
                                        className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ease-out motion-reduce:transition-none ${
                                            isOpen
                                                ? 'rotate-0 opacity-100'
                                                : 'rotate-90 opacity-0'
                                        }`}
                                    >
                                        −
                                    </span>
                                </span>
                            </button>
                        </h3>
                        {reduce ? (
                            isOpen && (
                                <div
                                    id={panelId}
                                    role="region"
                                    aria-labelledby={buttonId}
                                    className="px-4 pt-2 pb-7 text-lp-copy text-body-fg sm:pr-12 sm:pl-16"
                                >
                                    {item.a}
                                </div>
                            )
                        ) : (
                            <AnimatePresence initial={false}>
                                {isOpen && (
                                    <m.div
                                        id={panelId}
                                        role="region"
                                        aria-labelledby={buttonId}
                                        initial={{ height: 0 }}
                                        animate={{ height: 'auto' }}
                                        exit={{ height: 0 }}
                                        transition={{
                                            duration: 0.35,
                                            ease: [0.32, 0.72, 0, 1],
                                        }}
                                        className="overflow-hidden"
                                    >
                                        <p className="px-4 pt-2 pb-7 text-lp-copy text-body-fg sm:pr-12 sm:pl-16">
                                            {item.a}
                                        </p>
                                    </m.div>
                                )}
                            </AnimatePresence>
                        )}
                    </li>
                )
            })}
        </ul>
    )
}
